import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import {
  AlertCircle,
  Ban,
  Bot,
  Eye,
  FileText,
  LayoutTemplate,
  MailCheck,
  Megaphone,
  MousePointerClick,
  Send,
  ShieldOff,
  UserX,
  Users
} from "lucide-react";
import type {
  CommunicationActivityRow,
  CommunicationsAnalytics,
  CommunicationsOverview,
  EmailAutomationInput,
  EmailCampaignInput,
  EmailLayoutInput,
  EmailTemplateInput,
  MarketingSubscriberInput
} from "@booking-seconds/types";
import { Badge, Button, Card, DataTable, Field } from "@booking-seconds/ui";
import { DateInput } from "./date-input";
import { apiFetch } from "./lib/api";
import { OpsHeader, OpsMetricCard, OpsMetricGrid, OpsPageShell } from "./pages/admin/ops-ui";

type EmailLayoutRecord = EmailLayoutInput & {
  id: string;
  accountId: string;
  createdAt: string;
  updatedAt: string;
};

type EmailTemplateRecord = EmailTemplateInput & {
  id: string;
  accountId: string;
  layoutName: string;
  createdAt: string;
  updatedAt: string;
};

type MarketingSubscriberRecord = MarketingSubscriberInput & {
  id: string;
  accountId: string;
  consentCapturedAt: string;
  doubleOptInConfirmedAt: string;
  unsubscribedAt: string;
  lastEmailSentAt: string;
  createdAt: string;
  updatedAt: string;
};

type EmailSuppressionRecord = {
  id: string;
  email: string;
  reason: string;
  source: string;
  entityType: string;
  entityId: string;
  createdAt: string;
};

type EmailCampaignRecord = EmailCampaignInput & {
  id: string;
  accountId: string;
  templateName: string;
  status: "draft" | "scheduled" | "sending" | "sent" | "sent_with_errors" | "cancelled";
  scheduledFor: string;
  startedAt: string;
  completedAt: string;
  estimatedRecipientCount: number;
  sentCount: number;
  failedCount: number;
  createdAt: string;
  updatedAt: string;
};

type EmailAutomationRecord = EmailAutomationInput & {
  id: string;
  accountId: string;
  templateName: string;
  stepStats: Array<{
    stepNumber: number;
    stepDelayDays: number;
    pendingCount: number;
    queuedCount: number;
    sentCount: number;
    failedCount: number;
    convertedCount: number;
    conversionRate: number;
    nextDueAt: string;
    stepDelayMinutes: number;
    cancelledCount: number;
  }>;
  pendingCount: number;
  queuedCount: number;
  sentCount: number;
  failedCount: number;
  cancelledCount: number;
  nextDueAt: string;
  createdAt: string;
  updatedAt: string;
};

type LocationSettingsItem = {
  id: string;
  name: string;
  active?: number | boolean;
};

type TagSettingsItem = {
  id: string;
  name: string;
  color?: string;
  active?: number | boolean;
  entityType?: string;
};

type ServiceSettingsItem = {
  id: string;
  name: string;
  active?: number | boolean;
};

type AutomationDelayUnit = EmailAutomationInput["steps"][number]["delayUnit"];

const emptyLayoutDraft: EmailLayoutInput = {
  name: "New Layout",
  primaryColor: "#c65f3f",
  textColor: "#18261f",
  logoUrl: "",
  heroImageUrl: "",
  htmlShell:
    '<!doctype html><html><body style="margin:0;padding:0;background:#f6f0e6;font-family:Arial,sans-serif;color:#18261f;"><div style="max-width:640px;margin:0 auto;padding:32px 16px;"><div style="background:#ffffff;border-radius:28px;overflow:hidden;border:1px solid rgba(24,38,31,0.08);"><div style="padding:24px 28px;background:{{ primary_color }};color:#ffffff;"><h1 style="margin:0;font-size:28px;line-height:1.1;">{{ email_title }}</h1><p style="margin:10px 0 0;font-size:14px;opacity:0.86;">{{ preheader }}</p></div><div style="padding:28px;color:{{ text_color }};">{{ email_content }}</div><div style="padding:20px 28px;border-top:1px solid rgba(24,38,31,0.08);font-size:13px;color:#5d6c63;">{{ footer_html }}{{ unsubscribe_section }}</div></div></div></body></html>',
  footerHtml: '<p style="margin:0;">Questions? Reply to this email and the {{ business_name }} team will help.</p>',
  isDefault: false,
  active: true
};

const emptyTemplateDraft: EmailTemplateInput = {
  name: "Newsletter Update",
  key: "newsletter_update",
  templateType: "marketing",
  layoutId: "",
  category: "newsletter",
  subjectTemplate: "Fresh updates from {{ businessName }}",
  preheader: "A quick look at what is new this week.",
  bodyHtml: "<p>Hi {{ customer.name }},</p><p>We wanted to share a quick update from {{ businessName }}.</p>",
  bodyText: "",
  editor: {},
  fromNameOverride: "",
  fromEmailOverride: "",
  replyToOverride: "",
  active: true
};

const emptySubscriberDraft: MarketingSubscriberInput = {
  email: "",
  firstName: "",
  lastName: "",
  status: "subscribed",
  source: "admin_manual",
  sourceDetails: {},
  tags: []
};

const emptyCampaignDraft: EmailCampaignInput = {
  name: "April Newsletter",
  templateId: "",
  audienceMode: "subscribers",
  audienceFilters: {}
};

const emptyAutomationDraft: EmailAutomationInput = {
  name: "90 Day Re-Engagement",
  templateId: "",
  triggerType: "days_since_last_completed_booking",
  delayDays: 90,
  followUpDelays: [],
  stepTemplateIds: {},
  steps: [
    {
      id: "step_1",
      delayValue: 90,
      delayUnit: "days",
      templateId: "",
      generatedOffer: {
        enabled: false,
        discountType: "percentage",
        discountValue: 10,
        codePrefix: "",
        expirationDays: 7,
        eligibilityMode: "customer_winback"
      }
    }
  ],
  enrollmentPolicy: "all_eligible",
  audienceFilters: {
    tagIds: [],
    serviceFrequencies: [],
    locationIds: [],
    sourceServiceIds: [],
    targetServiceIds: []
  },
  active: true
};

type AnalyticsPreset = "7d" | "30d" | "90d" | "custom";

const analyticsPresetOptions: Array<{ value: AnalyticsPreset; label: string }> = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "custom", label: "Custom" }
];

function formatDateTime(value: string) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatDate(value: string) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function toDateInputValue(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseDateInput(value: string, endOfDay = false) {
  const parts = String(value ?? "")
    .trim()
    .split("-")
    .map((item) => Number(item));
  if (parts.length !== 3 || parts.some((item) => !Number.isFinite(item))) {
    return null;
  }

  const [year, month, day] = parts;
  const date = endOfDay ? new Date(year, month - 1, day, 23, 59, 59, 999) : new Date(year, month - 1, day, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function buildAnalyticsRange(preset: AnalyticsPreset, customStartDate: string, customEndDate: string) {
  if (preset === "custom") {
    const customStart = parseDateInput(customStartDate, false);
    const customEnd = parseDateInput(customEndDate, true);
    if (!customStart || !customEnd) {
      return null;
    }

    const [startDate, endDate] =
      customStart.getTime() <= customEnd.getTime() ? [customStart, customEnd] : [customEnd, customStart];
    return {
      preset,
      startAt: startDate.toISOString(),
      endAt: endDate.toISOString()
    };
  }

  const days = preset === "7d" ? 7 : preset === "90d" ? 90 : 30;
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (days - 1));
  startDate.setHours(0, 0, 0, 0);
  return {
    preset,
    startAt: startDate.toISOString(),
    endAt: endDate.toISOString()
  };
}

function formatAnalyticsRangeLabel(analytics: Pick<CommunicationsAnalytics, "preset" | "startAt" | "endAt"> | null) {
  if (!analytics) {
    return "Choose a valid date range to load analytics.";
  }

  if (analytics.preset === "7d") {
    return `Last 7 days • ${formatDate(analytics.startAt)} to ${formatDate(analytics.endAt)}`;
  }
  if (analytics.preset === "90d") {
    return `Last 90 days • ${formatDate(analytics.startAt)} to ${formatDate(analytics.endAt)}`;
  }
  if (analytics.preset === "custom") {
    return `Custom range • ${formatDate(analytics.startAt)} to ${formatDate(analytics.endAt)}`;
  }
  return `Last 30 days • ${formatDate(analytics.startAt)} to ${formatDate(analytics.endAt)}`;
}

function getDeliveryTone(value: string) {
  switch (String(value ?? "").trim().toLowerCase()) {
    case "delivered":
    case "opened":
    case "clicked":
      return "success" as const;
    case "sent":
      return "info" as const;
    case "delivery_delayed":
      return "warning" as const;
    case "bounced":
    case "complained":
    case "failed":
      return "danger" as const;
    default:
      return "neutral" as const;
  }
}

function getSourceFilterValue(item: Pick<CommunicationActivityRow, "campaignId" | "automationId" | "messageKind">) {
  if (item.campaignId) {
    return "campaign";
  }
  if (item.automationId) {
    return "automation";
  }
  return item.messageKind === "marketing" ? "marketing" : "transactional";
}

function getSourceFilterLabel(value: string) {
  switch (value) {
    case "campaign":
      return "Campaign";
    case "automation":
      return "Automation";
    case "marketing":
      return "Marketing";
    default:
      return "Transactional";
  }
}

function formatPercentage(value: number) {
  return `${Math.round((Number(value) || 0) * 100)}%`;
}

function toLocalDateTimeInput(value: string) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function fromLocalDateTimeInput(value: string) {
  if (!value) {
    return "";
  }

  return new Date(value).toISOString();
}

function renderPreviewTokens(template: string, values: Record<string, string>) {
  return String(template ?? "").replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_match, key) => values[key] ?? "");
}

function buildLayoutPreview(layout: EmailLayoutInput) {
  return renderPreviewTokens(layout.htmlShell, {
    primary_color: layout.primaryColor,
    text_color: layout.textColor,
    primaryColor: layout.primaryColor,
    textColor: layout.textColor,
    business_name: "Booking Seconds",
    businessName: "Booking Seconds",
    email_title: "Monthly Home Care Update",
    emailTitle: "Monthly Home Care Update",
    preheader: "Previewing how this layout wraps your email content.",
    email_content:
      "<p>Hi Morgan,</p><p>This is where your email template body will render.</p><p>Use this layout to keep your messages branded and consistent.</p>",
    emailContent:
      "<p>Hi Morgan,</p><p>This is where your email template body will render.</p><p>Use this layout to keep your messages branded and consistent.</p>",
    footer_html: layout.footerHtml,
    footerHtml: layout.footerHtml,
    unsubscribe_section: '<p style="margin:14px 0 0;"><a href="#" style="color:inherit;">Unsubscribe</a></p>',
    unsubscribeSection: '<p style="margin:14px 0 0;"><a href="#" style="color:inherit;">Unsubscribe</a></p>'
  });
}

function tagsToText(tags: string[]) {
  return tags.join(", ");
}

function parseTagsInput(value: string) {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function normalizeClientAutomationAudienceFilters(value: unknown): EmailAutomationInput["audienceFilters"] {
  const current = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  return {
    tagIds: asArray<unknown>(current.tagIds).map((item) => String(item ?? "").trim()).filter(Boolean),
    serviceFrequencies: asArray<unknown>(current.serviceFrequencies).map((item) => String(item ?? "").trim()).filter(Boolean),
    locationIds: asArray<unknown>(current.locationIds).map((item) => String(item ?? "").trim()).filter(Boolean),
    sourceServiceIds: asArray<unknown>(current.sourceServiceIds).map((item) => String(item ?? "").trim()).filter(Boolean),
    targetServiceIds: asArray<unknown>(current.targetServiceIds).map((item) => String(item ?? "").trim()).filter(Boolean)
  };
}

function normalizeClientAutomationFollowUpDelays(value: unknown) {
  return Array.from(
    new Set(
      asArray<unknown>(value)
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item >= 1 && item <= 3650)
        .map((item) => Math.round(item))
    )
  ).sort((left, right) => left - right);
}

function normalizeGeneratedOffer(
  value: unknown,
  eligibilityMode: "newsletter_first_booking" | "customer_winback" = "customer_winback"
): EmailAutomationInput["steps"][number]["generatedOffer"] {
  const current = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  return {
    enabled: Boolean(current.enabled),
    discountType: "percentage" as const,
    discountValue: Math.max(1, Math.min(100, Math.round(Number(current.discountValue ?? 10) || 10))),
    codePrefix: String(current.codePrefix ?? "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 24),
    expirationDays: Math.max(1, Math.min(365, Math.round(Number(current.expirationDays ?? 7) || 7))),
    eligibilityMode: current.eligibilityMode === "newsletter_first_booking" ? "newsletter_first_booking" as const : eligibilityMode
  };
}

function automationStepDelayToMinutes(step: EmailAutomationInput["steps"][number]) {
  const value = Math.max(1, Math.min(3650, Math.round(Number(step.delayValue) || 1)));
  if (step.delayUnit === "minutes") {
    return value;
  }
  if (step.delayUnit === "hours") {
    return value * 60;
  }
  return value * 24 * 60;
}

function formatAutomationDelay(step: Pick<EmailAutomationInput["steps"][number], "delayValue" | "delayUnit">) {
  const value = Math.max(1, Math.round(Number(step.delayValue) || 1));
  const unit = step.delayUnit === "minutes" || step.delayUnit === "hours" || step.delayUnit === "days" ? step.delayUnit : "days";
  const singular = unit.slice(0, -1);
  return `${value} ${value === 1 ? singular : unit}`;
}

function formatDelayMinutes(value: number) {
  const minutes = Math.max(1, Math.round(Number(value) || 1));
  if (minutes % (24 * 60) === 0) {
    const days = minutes / (24 * 60);
    return `${days} day${days === 1 ? "" : "s"}`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

function normalizeClientAutomationSteps(value: Partial<EmailAutomationInput> | null | undefined, fallbackTemplateId = "") {
  const rawSteps = asArray<unknown>(value?.steps)
    .map((item, index) => {
      const current = item && typeof item === "object" && !Array.isArray(item) ? (item as Record<string, unknown>) : {};
      const delayUnit: AutomationDelayUnit =
        current.delayUnit === "minutes" || current.delayUnit === "hours" || current.delayUnit === "days"
          ? current.delayUnit
          : "days";
      const delayValue = Math.max(1, Math.min(3650, Math.round(Number(current.delayValue) || 1)));
      return {
        id: String(current.id ?? `step_${index + 1}`),
        delayValue,
        delayUnit,
        templateId: String(current.templateId ?? "").trim(),
        generatedOffer: normalizeGeneratedOffer(current.generatedOffer, "customer_winback")
      };
    });

  const legacyDelays = Array.from(
    new Set([
      Math.max(1, Math.min(3650, Math.round(Number(value?.delayDays ?? emptyAutomationDraft.delayDays) || emptyAutomationDraft.delayDays))),
      ...normalizeClientAutomationFollowUpDelays(value?.followUpDelays)
    ])
  ).sort((left, right) => left - right);
  const legacyStepTemplateIds = normalizeClientAutomationStepTemplateIds(value?.stepTemplateIds);
  const legacySteps = legacyDelays.map((delayDays, index) => ({
    id: `step_${index + 1}`,
    delayValue: delayDays,
    delayUnit: "days" as const,
    templateId:
      index === 0
        ? String(value?.templateId ?? fallbackTemplateId ?? "")
        : String(legacyStepTemplateIds[String(index + 1)] ?? value?.templateId ?? fallbackTemplateId ?? ""),
    generatedOffer: normalizeGeneratedOffer({}, "customer_winback")
  }));

  const steps = rawSteps.length ? rawSteps : legacySteps;
  return [...steps]
    .map((step, index) => ({
      ...step,
      id: step.id || `step_${index + 1}`,
      templateId: step.templateId || (index === 0 ? String(value?.templateId ?? fallbackTemplateId ?? "") : ""),
      generatedOffer: normalizeGeneratedOffer(step.generatedOffer, "customer_winback")
    }))
    .sort((left, right) => automationStepDelayToMinutes(left) - automationStepDelayToMinutes(right));
}

function getAutomationSequenceSteps(automation: Partial<EmailAutomationInput>) {
  return normalizeClientAutomationSteps(automation).map((step, index) => ({
    ...step,
    stepNumber: index + 1,
    delayMinutes: automationStepDelayToMinutes(step)
  }));
}

function normalizeClientAutomationStepTemplateIds(
  value: unknown,
  allowedStepNumbers?: number[]
): EmailAutomationInput["stepTemplateIds"] {
  const current = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  const allowed = allowedStepNumbers?.length ? new Set(allowedStepNumbers.map((item) => String(item))) : null;

  return Object.fromEntries(
    Object.entries(current)
      .map(([stepNumber, templateId]) => [String(stepNumber).trim(), String(templateId ?? "").trim()] as const)
      .filter(([stepNumber, templateId]) => /^\d+$/.test(stepNumber) && templateId && (!allowed || allowed.has(stepNumber)))
      .sort((left, right) => Number(left[0]) - Number(right[0]))
  );
}

function normalizeClientAutomationDraft(
  value: Partial<EmailAutomationInput> | null | undefined,
  fallbackTemplateId = ""
): EmailAutomationInput {
  const delayDays = Math.max(1, Math.min(3650, Number(value?.delayDays ?? emptyAutomationDraft.delayDays) || emptyAutomationDraft.delayDays));
  const followUpDelays = normalizeClientAutomationFollowUpDelays(value?.followUpDelays);
  const steps = normalizeClientAutomationSteps(value, fallbackTemplateId);
  const sequenceSteps = getAutomationSequenceSteps({ steps });
  const primaryTemplateId = String(value?.templateId ?? steps[0]?.templateId ?? fallbackTemplateId ?? "");

  return {
    ...emptyAutomationDraft,
    ...value,
    templateId: primaryTemplateId,
    triggerType:
      value?.triggerType === "days_since_cancelled_booking" ||
      value?.triggerType === "days_since_no_show" ||
      value?.triggerType === "first_completed_booking" ||
      value?.triggerType === "service_upsell_after_completed_booking"
        ? value.triggerType
        : "days_since_last_completed_booking",
    delayDays: Math.max(1, Math.round((sequenceSteps[0]?.delayMinutes ?? delayDays * 24 * 60) / (24 * 60))),
    followUpDelays: sequenceSteps.slice(1).map((step) => Math.max(1, Math.round(step.delayMinutes / (24 * 60)))),
    stepTemplateIds: normalizeClientAutomationStepTemplateIds(
      value?.stepTemplateIds,
      sequenceSteps.map((step) => step.stepNumber).filter((stepNumber) => stepNumber > 1)
    ),
    steps,
    enrollmentPolicy: value?.enrollmentPolicy === "future_only" ? "future_only" : "all_eligible",
    audienceFilters: normalizeClientAutomationAudienceFilters(value?.audienceFilters),
    active: value?.active ?? true
  };
}

function formatFilterList(values: string[]) {
  return values.join(", ");
}

function parseFilterList(value: string) {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function describeAutomationTrigger(triggerType: EmailAutomationInput["triggerType"], delayDays: number) {
  if (triggerType === "first_completed_booking") {
    return "after first completed visit";
  }
  if (triggerType === "service_upsell_after_completed_booking") {
    return "after a matching service visit";
  }
  if (triggerType === "days_since_cancelled_booking") {
    return `${delayDays} days after a cancelled booking`;
  }
  if (triggerType === "days_since_no_show") {
    return `${delayDays} days after a no-show`;
  }
  return `${delayDays} days after completed booking`;
}

function describeAutomationSequence(automation: Partial<EmailAutomationInput>) {
  return getAutomationSequenceSteps(automation)
    .map((step) => `${step.stepNumber}. ${formatAutomationDelay(step)}`)
    .join(", ");
}

function describeAutomationFilters(filters: EmailAutomationInput["audienceFilters"]) {
  const parts = [
    filters.tagIds.length ? `${filters.tagIds.length} tag filter(s)` : "",
    filters.serviceFrequencies.length ? `${filters.serviceFrequencies.length} frequency filter(s)` : "",
    filters.locationIds.length ? `${filters.locationIds.length} location filter(s)` : "",
    filters.sourceServiceIds.length ? `${filters.sourceServiceIds.length} source service filter(s)` : "",
    filters.targetServiceIds.length ? `${filters.targetServiceIds.length} target service filter(s)` : ""
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "all eligible customers";
}

export function CommunicationsPage() {
  return (
    <OpsPageShell>
      <OpsHeader
        eyebrow="Messaging operations"
        title="Communications"
        description="Manage branded email layouts, templates, newsletter subscribers, and delivery activity."
      />
      <div className="communications-tabs">
        {[
          ["overview", "Overview"],
          ["layouts", "Layouts"],
          ["templates", "Templates"],
          ["campaigns", "Campaigns"],
          ["automations", "Automations"],
          ["audience", "Audience"],
          ["activity", "Activity"]
        ].map(([path, label]) => (
          <NavLink key={path} to={`/admin/communications/${path}`} className={({ isActive }) => `communications-tab ${isActive ? "is-active" : ""}`}>
            {label}
          </NavLink>
        ))}
      </div>

      <Routes>
        <Route index element={<Navigate to="/admin/communications/overview" replace />} />
        <Route path="overview" element={<CommunicationsOverviewSection />} />
        <Route path="layouts" element={<CommunicationsLayoutsSection />} />
        <Route path="templates" element={<CommunicationsTemplatesSection />} />
        <Route path="campaigns" element={<CommunicationsCampaignsSection />} />
        <Route path="automations" element={<CommunicationsAutomationsSection />} />
        <Route path="audience" element={<CommunicationsAudienceSection />} />
        <Route path="activity" element={<CommunicationsActivitySection />} />
        <Route path="*" element={<Navigate to="/admin/communications/overview" replace />} />
      </Routes>
    </OpsPageShell>
  );
}

function CommunicationsOverviewSection() {
  const [analyticsPreset, setAnalyticsPreset] = useState<AnalyticsPreset>("30d");
  const [customStartDate, setCustomStartDate] = useState(() => {
    const value = new Date();
    value.setDate(value.getDate() - 29);
    return toDateInputValue(value);
  });
  const [customEndDate, setCustomEndDate] = useState(() => toDateInputValue(new Date()));
  const overview = useQuery({
    queryKey: ["communications", "overview"],
    queryFn: () => apiFetch<CommunicationsOverview>("/api/communications/overview")
  });
  const activity = useQuery({
    queryKey: ["communications", "activity"],
    queryFn: () => apiFetch<{ items: CommunicationActivityRow[] }>("/api/communications/activity")
  });
  const analyticsRange = useMemo(
    () => buildAnalyticsRange(analyticsPreset, customStartDate, customEndDate),
    [analyticsPreset, customStartDate, customEndDate]
  );
  const analytics = useQuery({
    queryKey: [
      "communications",
      "analytics",
      analyticsRange?.preset ?? analyticsPreset,
      analyticsRange?.startAt ?? customStartDate,
      analyticsRange?.endAt ?? customEndDate
    ],
    enabled: Boolean(analyticsRange),
    queryFn: () =>
      apiFetch<CommunicationsAnalytics>(
        `/api/communications/analytics?${new URLSearchParams({
          preset: analyticsRange!.preset,
          startAt: analyticsRange!.startAt,
          endAt: analyticsRange!.endAt
        }).toString()}`
      )
  });

  if (!overview.data) {
    return <div className="page-loading">Loading communications overview...</div>;
  }

  return (
    <div className="stack">
      <OpsMetricGrid>
        <OpsMetricCard label="Layouts" value={overview.data.layoutCount} detail="Brand email wrappers" icon={LayoutTemplate} tone="blue" />
        <OpsMetricCard label="Templates" value={overview.data.templateCount} detail="Reusable email content" icon={FileText} tone="violet" />
        <OpsMetricCard label="Campaigns" value={overview.data.campaignCount} detail={`${overview.data.scheduledCampaignCount} scheduled`} icon={Megaphone} tone="amber" />
        <OpsMetricCard label="Automations" value={overview.data.automationCount} detail={`${overview.data.activeAutomationCount} active`} icon={Bot} tone="emerald" />
        <OpsMetricCard label="Active subscribers" value={overview.data.subscriberCount} detail="Marketing audience" icon={Users} tone="blue" />
        <OpsMetricCard label="Suppressions" value={overview.data.suppressedCount} detail="Blocked from marketing sends" icon={ShieldOff} tone="rose" />
        <OpsMetricCard label="Sent (30 days)" value={overview.data.sentLast30Days} detail="Recent message volume" icon={Send} tone="emerald" />
        <OpsMetricCard label="Failed (30 days)" value={overview.data.failedLast30Days} detail="Needs delivery review" icon={AlertCircle} tone="rose" />
        <OpsMetricCard label="Unsubscribed (30 days)" value={overview.data.unsubscribedLast30Days} detail="Recent audience opt-outs" icon={UserX} tone="amber" />
      </OpsMetricGrid>

        <div className="grid two">
          <Card title="Recently Updated Templates" eyebrow="Library">
            {overview.data.recentTemplates.length ? (
              <DataTable
                columns={["Template", "Type", "Updated"]}
              rows={overview.data.recentTemplates.map((item) => [
                item.name,
                <Badge key={`${item.id}-type`} tone={item.templateType === "marketing" ? "warning" : "info"}>
                  {item.templateType}
                </Badge>,
                formatDateTime(item.updatedAt)
              ])}
            />
          ) : (
            <p className="card-copy">No email templates have been created yet.</p>
          )}
        </Card>

        <Card title="Recent Delivery Activity" eyebrow="Message log">
          {activity.data?.items?.length ? (
            <DataTable
              columns={["When", "Recipient", "Template", "Delivery"]}
              rows={activity.data.items.slice(0, 8).map((item) => [
                formatDateTime(item.bucket),
                item.recipient || "-",
                item.templateKey,
                <Badge key={item.id} tone={getDeliveryTone(item.deliveryStatus || item.status)}>
                  {item.deliveryStatus || item.status}
                </Badge>
              ])}
            />
          ) : (
            <p className="card-copy">No communications have been logged yet.</p>
            )}
          </Card>
        </div>

        <Card title="Performance Analytics" eyebrow="Date range">
          <div className="stack">
            <div className="communications-analytics-toolbar">
              <div className="communications-analytics-presets">
                {analyticsPresetOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant={analyticsPreset === option.value ? "primary" : "secondary"}
                    onClick={() => setAnalyticsPreset(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
              {analyticsPreset === "custom" ? (
                <div className="communications-analytics-custom">
                  <Field label="Start date">
                    <DateInput value={customStartDate} onChange={(event) => setCustomStartDate(event.target.value)} />
                  </Field>
                  <Field label="End date">
                    <DateInput value={customEndDate} onChange={(event) => setCustomEndDate(event.target.value)} />
                  </Field>
                </div>
              ) : null}
            </div>

            <p className="communications-analytics-meta">{formatAnalyticsRangeLabel(analytics.data ?? analyticsRange)}</p>

            {!analyticsRange ? (
              <p className="field-error">Choose both a start and end date to load analytics.</p>
            ) : analytics.isLoading ? (
              <p className="card-copy">Loading communications analytics...</p>
            ) : analytics.data ? (
              <>
                <OpsMetricGrid>
                  <OpsMetricCard label="Sent" value={analytics.data.summary.sentCount} detail="Messages attempted" icon={Send} tone="blue" />
                  <OpsMetricCard label="Delivered" value={analytics.data.summary.deliveredCount} detail="Accepted by recipient servers" icon={MailCheck} tone="emerald" />
                  <OpsMetricCard label="Opened" value={analytics.data.summary.openedCount} detail="Tracked opens" icon={Eye} tone="violet" />
                  <OpsMetricCard label="Clicked" value={analytics.data.summary.clickedCount} detail="Tracked clicks" icon={MousePointerClick} tone="amber" />
                  <OpsMetricCard label="Bounced" value={analytics.data.summary.bouncedCount} detail="Bounced deliveries" icon={Ban} tone="rose" />
                  <OpsMetricCard label="Complained" value={analytics.data.summary.complainedCount} detail="Spam complaint events" icon={ShieldOff} tone="rose" />
                  <OpsMetricCard label="Failed" value={analytics.data.summary.failedCount} detail="Failed send attempts" icon={AlertCircle} tone="rose" />
                  <OpsMetricCard label="Unsubscribed" value={analytics.data.summary.unsubscribedCount} detail="Opt-outs in this range" icon={UserX} tone="amber" />
                </OpsMetricGrid>

                <Card title="Campaign Performance" eyebrow="Campaigns" className="communications-analytics-card">
                  {analytics.data.campaigns.length ? (
                    <DataTable
                      columns={["Campaign", "Sent", "Delivered", "Opened", "Clicked", "Bounced", "Complained", "Failed"]}
                      rows={analytics.data.campaigns.map((item) => [
                        item.campaignName,
                        item.sentCount,
                        item.deliveredCount,
                        item.openedCount,
                        item.clickedCount,
                        item.bouncedCount,
                        item.complainedCount,
                        item.failedCount
                      ])}
                    />
                  ) : (
                    <p className="card-copy">No campaign sends landed inside this date range yet.</p>
                  )}
                </Card>

                <Card title="Automation Step Performance" eyebrow="Automations" className="communications-analytics-card">
                  {analytics.data.automations.length ? (
                    <DataTable
                      columns={["Automation", "Step", "Sent", "Delivered", "Opened", "Clicked", "Failed", "Rebooked"]}
                      rows={analytics.data.automations.map((item) => [
                        item.automationName,
                        `Step ${item.stepNumber} • ${item.stepDelayDays} day${item.stepDelayDays === 1 ? "" : "s"}`,
                        item.sentCount,
                        item.deliveredCount,
                        item.openedCount,
                        item.clickedCount,
                        item.failedCount,
                        `${item.convertedCount} (${formatPercentage(item.conversionRate)})`
                      ])}
                    />
                  ) : (
                    <p className="card-copy">No automation sends landed inside this date range yet.</p>
                  )}
                </Card>
              </>
            ) : (
              <p className="card-copy">Analytics are temporarily unavailable.</p>
            )}
          </div>
        </Card>
      </div>
    );
  }

function CommunicationsLayoutsSection() {
  const queryClient = useQueryClient();
  const layouts = useQuery({
    queryKey: ["communications", "layouts"],
    queryFn: () => apiFetch<{ items: EmailLayoutRecord[] }>("/api/communications/layouts")
  });
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<EmailLayoutInput>(emptyLayoutDraft);

  useEffect(() => {
    if (layouts.data?.items?.length && !selectedId) {
      setSelectedId(layouts.data.items[0].id);
    }
  }, [layouts.data, selectedId]);

  useEffect(() => {
    const selected = layouts.data?.items.find((item) => item.id === selectedId);
    if (selected) {
      setDraft({
        name: selected.name,
        primaryColor: selected.primaryColor,
        textColor: selected.textColor,
        logoUrl: selected.logoUrl,
        heroImageUrl: selected.heroImageUrl,
        htmlShell: selected.htmlShell,
        footerHtml: selected.footerHtml,
        isDefault: selected.isDefault,
        active: selected.active
      });
    } else {
      setDraft(emptyLayoutDraft);
    }
  }, [layouts.data, selectedId]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<EmailLayoutRecord>(selectedId ? `/api/communications/layouts/${selectedId}` : "/api/communications/layouts", {
        method: selectedId ? "PUT" : "POST",
        body: JSON.stringify(draft)
      }),
    onSuccess: async (layout) => {
      setSelectedId(layout.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "layouts"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "templates"] })
      ]);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch<{ success: boolean }>(`/api/communications/layouts/${selectedId}`, { method: "DELETE" }),
    onSuccess: async () => {
      setSelectedId("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "layouts"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  return (
    <div className="communications-shell">
      <Card
        title="Layouts"
        eyebrow="Master templates"
        actions={<Button variant="secondary" onClick={() => setSelectedId("")}>New layout</Button>}
      >
        <div className="communications-list">
          {(layouts.data?.items ?? []).map((item) => (
            <button
              key={item.id}
              type="button"
              className={`communications-list__item ${selectedId === item.id ? "is-active" : ""}`}
              onClick={() => setSelectedId(item.id)}
            >
              <div>
                <strong>{item.name}</strong>
                <p>{item.isDefault ? "Default layout" : "Custom layout"}</p>
              </div>
              <Badge tone={item.active ? "success" : "warning"}>{item.active ? "Active" : "Inactive"}</Badge>
            </button>
          ))}
        </div>
      </Card>

      <div className="stack">
        <Card
          title={selectedId ? "Edit Layout" : "Create Layout"}
          eyebrow="Brand wrapper"
          actions={
            <div className="communications-card-actions">
              {selectedId ? (
                <Button
                  variant="ghost"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending || saveMutation.isPending}
                >
                  Delete
                </Button>
              ) : null}
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || deleteMutation.isPending}>
                {saveMutation.isPending ? "Saving..." : "Save layout"}
              </Button>
            </div>
          }
        >
          <div className="grid two">
            <Field label="Name"><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
            <Field label="Primary color"><input value={draft.primaryColor} onChange={(event) => setDraft({ ...draft, primaryColor: event.target.value })} /></Field>
            <Field label="Text color"><input value={draft.textColor} onChange={(event) => setDraft({ ...draft, textColor: event.target.value })} /></Field>
            <Field label="Logo URL"><input value={draft.logoUrl} onChange={(event) => setDraft({ ...draft, logoUrl: event.target.value })} /></Field>
            <Field label="Hero image URL"><input value={draft.heroImageUrl} onChange={(event) => setDraft({ ...draft, heroImageUrl: event.target.value })} /></Field>
            <Field label="Default layout">
              <label className="checkbox-row">
                <input type="checkbox" checked={draft.isDefault} onChange={(event) => setDraft({ ...draft, isDefault: event.target.checked })} />
                <span>Use this layout by default.</span>
              </label>
            </Field>
            <Field label="Active">
              <label className="checkbox-row">
                <input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} />
                <span>Make this layout available.</span>
              </label>
            </Field>
          </div>
          <Field label="HTML shell" hint="Use placeholders like {{ email_title }}, {{ email_content }}, and {{ unsubscribe_section }}.">
            <textarea rows={12} value={draft.htmlShell} onChange={(event) => setDraft({ ...draft, htmlShell: event.target.value })} />
          </Field>
          <Field label="Footer HTML">
            <textarea rows={4} value={draft.footerHtml} onChange={(event) => setDraft({ ...draft, footerHtml: event.target.value })} />
          </Field>
          {saveMutation.isError ? <p className="error-text">{String(saveMutation.error)}</p> : null}
          {deleteMutation.isError ? <p className="error-text">{String(deleteMutation.error)}</p> : null}
        </Card>

        <Card title="Layout Preview" eyebrow="Sample render">
          <iframe className="communications-preview-frame" title="Layout preview" srcDoc={buildLayoutPreview(draft)} />
        </Card>
      </div>
    </div>
  );
}

function CommunicationsTemplatesSection() {
  const queryClient = useQueryClient();
  const templates = useQuery({
    queryKey: ["communications", "templates"],
    queryFn: () => apiFetch<{ items: EmailTemplateRecord[] }>("/api/communications/templates")
  });
  const layouts = useQuery({
    queryKey: ["communications", "layouts"],
    queryFn: () => apiFetch<{ items: EmailLayoutRecord[] }>("/api/communications/layouts")
  });
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<EmailTemplateInput>(emptyTemplateDraft);
  const [testTo, setTestTo] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");

  useEffect(() => {
    if (templates.data?.items?.length && !selectedId) {
      setSelectedId(templates.data.items[0].id);
    }
  }, [templates.data, selectedId]);

  useEffect(() => {
    const selected = templates.data?.items.find((item) => item.id === selectedId);
    if (selected) {
      setDraft({
        name: selected.name,
        key: selected.key,
        templateType: selected.templateType,
        layoutId: selected.layoutId,
        category: selected.category,
        subjectTemplate: selected.subjectTemplate,
        preheader: selected.preheader,
        bodyHtml: selected.bodyHtml,
        bodyText: selected.bodyText,
        editor: selected.editor,
        fromNameOverride: selected.fromNameOverride,
        fromEmailOverride: selected.fromEmailOverride,
        replyToOverride: selected.replyToOverride,
        active: selected.active
      });
      setPreviewHtml("");
      setPreviewSubject("");
    } else {
      setDraft(emptyTemplateDraft);
    }
  }, [templates.data, selectedId]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<EmailTemplateRecord>(selectedId ? `/api/communications/templates/${selectedId}` : "/api/communications/templates", {
        method: selectedId ? "PUT" : "POST",
        body: JSON.stringify(draft)
      }),
    onSuccess: async (template) => {
      setSelectedId(template.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "templates"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity"] })
      ]);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch<{ success: boolean }>(`/api/communications/templates/${selectedId}`, { method: "DELETE" }),
    onSuccess: async () => {
      setSelectedId("");
      setPreviewHtml("");
      setPreviewSubject("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "templates"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const previewMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ subject: string; html: string }>(`/api/communications/templates/${selectedId}/preview`, {
        method: "POST",
        body: JSON.stringify({
          sampleRecipientEmail: testTo || "",
          variables: {}
        })
      }),
    onSuccess: (payload) => {
      setPreviewSubject(payload.subject);
      setPreviewHtml(payload.html);
    }
  });

  const testMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ success: boolean; id?: string }>(`/api/communications/templates/${selectedId}/test`, {
        method: "POST",
        body: JSON.stringify({
          to: testTo,
          sampleRecipientEmail: testTo || ""
        })
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["communications", "activity"] });
    }
  });

  const layoutOptions = layouts.data?.items ?? [];

  return (
    <div className="communications-shell">
      <Card title="Templates" eyebrow="Email library" actions={<Button variant="secondary" onClick={() => setSelectedId("")}>New template</Button>}>
        <div className="communications-list">
          {(templates.data?.items ?? []).map((item) => (
            <button
              key={item.id}
              type="button"
              className={`communications-list__item ${selectedId === item.id ? "is-active" : ""}`}
              onClick={() => setSelectedId(item.id)}
            >
              <div>
                <strong>{item.name}</strong>
                <p>{item.layoutName || "No layout selected"}</p>
              </div>
              <Badge tone={item.templateType === "marketing" ? "warning" : "info"}>{item.templateType}</Badge>
            </button>
          ))}
        </div>
      </Card>

      <div className="stack">
        <Card
          title={selectedId ? "Edit Template" : "Create Template"}
          eyebrow="Content"
          actions={
            <div className="communications-card-actions">
              {selectedId ? (
                <Button variant="ghost" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending || saveMutation.isPending}>
                  Delete
                </Button>
              ) : null}
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || deleteMutation.isPending}>
                {saveMutation.isPending ? "Saving..." : "Save template"}
              </Button>
            </div>
          }
        >
          <div className="grid two">
            <Field label="Name"><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
            <Field label="Key"><input value={draft.key} onChange={(event) => setDraft({ ...draft, key: event.target.value })} /></Field>
            <Field label="Template type">
              <select value={draft.templateType} onChange={(event) => setDraft({ ...draft, templateType: event.target.value as EmailTemplateInput["templateType"] })}>
                <option value="marketing">Marketing</option>
                <option value="transactional">Transactional</option>
              </select>
            </Field>
            <Field label="Layout">
              <select value={draft.layoutId ?? ""} onChange={(event) => setDraft({ ...draft, layoutId: event.target.value })}>
                <option value="">Default layout</option>
                {layoutOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Category"><input value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })} /></Field>
            <Field label="Active">
              <label className="checkbox-row">
                <input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} />
                <span>Use this template in the library.</span>
              </label>
            </Field>
          </div>
          <Field label="Subject">
            <input value={draft.subjectTemplate} onChange={(event) => setDraft({ ...draft, subjectTemplate: event.target.value })} />
          </Field>
          <Field label="Preheader">
            <input value={draft.preheader} onChange={(event) => setDraft({ ...draft, preheader: event.target.value })} />
          </Field>
          <Field label="Body HTML">
            <textarea rows={10} value={draft.bodyHtml} onChange={(event) => setDraft({ ...draft, bodyHtml: event.target.value })} />
          </Field>
          <Field label="Body Text">
            <textarea rows={5} value={draft.bodyText} onChange={(event) => setDraft({ ...draft, bodyText: event.target.value })} />
          </Field>
          <div className="grid two">
            <Field label="From name override"><input value={draft.fromNameOverride} onChange={(event) => setDraft({ ...draft, fromNameOverride: event.target.value })} /></Field>
            <Field label="From email override"><input value={draft.fromEmailOverride} onChange={(event) => setDraft({ ...draft, fromEmailOverride: event.target.value })} /></Field>
            <Field label="Reply-to override"><input value={draft.replyToOverride} onChange={(event) => setDraft({ ...draft, replyToOverride: event.target.value })} /></Field>
          </div>
          {saveMutation.isError ? <p className="error-text">{String(saveMutation.error)}</p> : null}
          {deleteMutation.isError ? <p className="error-text">{String(deleteMutation.error)}</p> : null}
        </Card>

        <Card title="Preview And Test" eyebrow="Saved templates">
          <div className="grid two">
            <Field label="Send preview/test to">
              <input value={testTo} onChange={(event) => setTestTo(event.target.value)} placeholder="name@example.com" />
            </Field>
          </div>
          <div className="communications-card-actions">
            <Button variant="secondary" onClick={() => previewMutation.mutate()} disabled={!selectedId || previewMutation.isPending}>
              {previewMutation.isPending ? "Rendering..." : "Preview template"}
            </Button>
            <Button onClick={() => testMutation.mutate()} disabled={!selectedId || !testTo || testMutation.isPending}>
              {testMutation.isPending ? "Sending..." : "Send test email"}
            </Button>
          </div>
          {previewMutation.isError ? <p className="error-text">{String(previewMutation.error)}</p> : null}
          {testMutation.isError ? <p className="error-text">{String(testMutation.error)}</p> : null}
          {previewSubject ? <p className="card-copy"><strong>Subject:</strong> {previewSubject}</p> : null}
          {previewHtml ? (
            <iframe className="communications-preview-frame" title="Template preview" srcDoc={previewHtml} />
          ) : (
            <p className="card-copy">Save or select a template, then render a preview here.</p>
          )}
        </Card>
      </div>
    </div>
  );
}

function CommunicationsCampaignsSection() {
  const queryClient = useQueryClient();
  const campaigns = useQuery({
    queryKey: ["communications", "campaigns"],
    queryFn: () => apiFetch<{ items: EmailCampaignRecord[] }>("/api/communications/campaigns")
  });
  const templates = useQuery({
    queryKey: ["communications", "templates"],
    queryFn: () => apiFetch<{ items: EmailTemplateRecord[] }>("/api/communications/templates")
  });
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<EmailCampaignInput>(emptyCampaignDraft);
  const [scheduleFor, setScheduleFor] = useState("");
  const [estimatedRecipients, setEstimatedRecipients] = useState<number | null>(null);

  const marketingTemplates = (templates.data?.items ?? []).filter((item) => item.templateType === "marketing");

  useEffect(() => {
    if (campaigns.data?.items?.length && !selectedId) {
      setSelectedId(campaigns.data.items[0].id);
    }
  }, [campaigns.data, selectedId]);

  useEffect(() => {
    if (!selectedId && marketingTemplates.length && !draft.templateId) {
      setDraft((current) => ({
        ...current,
        templateId: marketingTemplates[0].id
      }));
    }
  }, [selectedId, marketingTemplates, draft.templateId]);

  useEffect(() => {
    const selected = campaigns.data?.items.find((item) => item.id === selectedId);
    if (selected) {
      setDraft({
        name: selected.name,
        templateId: selected.templateId,
        audienceMode: selected.audienceMode,
        audienceFilters: selected.audienceFilters
      });
      setScheduleFor(toLocalDateTimeInput(selected.scheduledFor));
      setEstimatedRecipients(selected.estimatedRecipientCount);
    } else {
      setDraft((current) => ({
        ...emptyCampaignDraft,
        templateId: current.templateId || marketingTemplates[0]?.id || ""
      }));
      setScheduleFor("");
      setEstimatedRecipients(null);
    }
  }, [campaigns.data, selectedId, marketingTemplates]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<EmailCampaignRecord>(selectedId ? `/api/communications/campaigns/${selectedId}` : "/api/communications/campaigns", {
        method: selectedId ? "PUT" : "POST",
        body: JSON.stringify(draft)
      }),
    onSuccess: async (campaign) => {
      setSelectedId(campaign.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "campaigns"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch<{ success: boolean }>(`/api/communications/campaigns/${selectedId}`, { method: "DELETE" }),
    onSuccess: async () => {
      setSelectedId("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "campaigns"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const estimateMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ totalRecipients: number }>("/api/communications/campaigns/estimate", {
        method: "POST",
        body: JSON.stringify({
          audienceMode: draft.audienceMode
        })
      }),
    onSuccess: (payload) => {
      setEstimatedRecipients(payload.totalRecipients);
    }
  });

  const sendMutation = useMutation({
    mutationFn: () => apiFetch<{ success: boolean; queued: number }>(`/api/communications/campaigns/${selectedId}/send`, { method: "POST" }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "campaigns"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "activity"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const scheduleMutation = useMutation({
    mutationFn: () =>
      apiFetch<EmailCampaignRecord>(`/api/communications/campaigns/${selectedId}/schedule`, {
        method: "POST",
        body: JSON.stringify({
          scheduledFor: fromLocalDateTimeInput(scheduleFor)
        })
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "campaigns"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiFetch<EmailCampaignRecord>(`/api/communications/campaigns/${selectedId}/cancel`, { method: "POST" }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "campaigns"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const selectedCampaign = campaigns.data?.items.find((item) => item.id === selectedId) ?? null;
  const canEditCampaign = !selectedCampaign || selectedCampaign.status === "draft" || selectedCampaign.status === "scheduled";
  const canSendCampaign = Boolean(selectedCampaign && (selectedCampaign.status === "draft" || selectedCampaign.status === "scheduled"));
  const canScheduleCampaign = Boolean(selectedCampaign && (selectedCampaign.status === "draft" || selectedCampaign.status === "scheduled"));

  return (
    <div className="communications-shell">
      <Card title="Campaigns" eyebrow="Newsletters" actions={<Button variant="secondary" onClick={() => setSelectedId("")}>New campaign</Button>}>
        <div className="communications-list">
          {(campaigns.data?.items ?? []).map((item) => (
            <button
              key={item.id}
              type="button"
              className={`communications-list__item ${selectedId === item.id ? "is-active" : ""}`}
              onClick={() => setSelectedId(item.id)}
            >
              <div>
                <strong>{item.name}</strong>
                <p>{item.templateName || "No template"} - {item.estimatedRecipientCount} recipients</p>
              </div>
              <Badge tone={item.status === "sent" ? "success" : item.status === "scheduled" ? "warning" : item.status === "sent_with_errors" ? "danger" : "info"}>
                {item.status}
              </Badge>
            </button>
          ))}
        </div>
      </Card>

      <div className="stack">
        <Card
          title={selectedId ? "Edit Campaign" : "Create Campaign"}
          eyebrow="Audience"
          actions={
            <div className="communications-card-actions">
              {selectedId ? (
                <Button variant="ghost" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending || saveMutation.isPending}>
                  Delete
                </Button>
              ) : null}
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !draft.templateId || !canEditCampaign}>
                {saveMutation.isPending ? "Saving..." : "Save campaign"}
              </Button>
            </div>
          }
        >
          <div className="grid two">
            <Field label="Campaign name">
              <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </Field>
            <Field label="Template">
              <select value={draft.templateId} onChange={(event) => setDraft({ ...draft, templateId: event.target.value })}>
                <option value="">Choose a template</option>
                {marketingTemplates.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Audience">
              <select value={draft.audienceMode} onChange={(event) => setDraft({ ...draft, audienceMode: event.target.value as EmailCampaignInput["audienceMode"] })}>
                <option value="subscribers">Newsletter subscribers</option>
                <option value="customers">Customers with marketing opt-in</option>
                <option value="all_contacts">All eligible contacts</option>
              </select>
            </Field>
          </div>
          <div className="communications-card-actions">
            <Button variant="secondary" onClick={() => estimateMutation.mutate()} disabled={estimateMutation.isPending}>
              {estimateMutation.isPending ? "Estimating..." : "Estimate audience"}
            </Button>
          </div>
          {estimatedRecipients != null ? <p className="card-copy">Estimated recipients: <strong>{estimatedRecipients}</strong></p> : null}
          {selectedCampaign && !canEditCampaign ? <p className="card-copy">This campaign is locked because it has already started or finished sending.</p> : null}
          {saveMutation.isError ? <p className="error-text">{String(saveMutation.error)}</p> : null}
          {deleteMutation.isError ? <p className="error-text">{String(deleteMutation.error)}</p> : null}
        </Card>

        <Card title="Send Options" eyebrow="Dispatch">
          <div className="grid two">
            <Field label="Schedule for">
              <input type="datetime-local" value={scheduleFor} onChange={(event) => setScheduleFor(event.target.value)} />
            </Field>
          </div>
          <div className="communications-card-actions">
            <Button onClick={() => sendMutation.mutate()} disabled={!canSendCampaign || sendMutation.isPending}>
              {sendMutation.isPending ? "Queueing..." : "Send now"}
            </Button>
            <Button variant="secondary" onClick={() => scheduleMutation.mutate()} disabled={!canScheduleCampaign || !scheduleFor || scheduleMutation.isPending}>
              {scheduleMutation.isPending ? "Scheduling..." : "Schedule send"}
            </Button>
            {selectedCampaign?.status === "scheduled" ? (
              <Button variant="ghost" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>
                Cancel schedule
              </Button>
            ) : null}
          </div>
          {selectedCampaign ? (
            <div className="grid three">
              <div className="booking-meta-card">
                <p className="eyebrow">Status</p>
                <strong>{selectedCampaign.status}</strong>
              </div>
              <div className="booking-meta-card">
                <p className="eyebrow">Recipients</p>
                <strong>{selectedCampaign.estimatedRecipientCount}</strong>
              </div>
              <div className="booking-meta-card">
                <p className="eyebrow">Results</p>
                <strong>{selectedCampaign.sentCount} sent / {selectedCampaign.failedCount} failed</strong>
              </div>
            </div>
          ) : (
            <p className="card-copy">Save the campaign first, then send immediately or schedule it.</p>
          )}
          {selectedCampaign?.scheduledFor ? <p className="card-copy">Scheduled for {formatDateTime(selectedCampaign.scheduledFor)}</p> : null}
          {sendMutation.isError ? <p className="error-text">{String(sendMutation.error)}</p> : null}
          {scheduleMutation.isError ? <p className="error-text">{String(scheduleMutation.error)}</p> : null}
          {cancelMutation.isError ? <p className="error-text">{String(cancelMutation.error)}</p> : null}
        </Card>
      </div>
    </div>
  );
}

function CommunicationsAutomationsSection() {
  const queryClient = useQueryClient();
  const automations = useQuery({
    queryKey: ["communications", "automations"],
    queryFn: () => apiFetch<{ items: EmailAutomationRecord[] }>("/api/communications/automations")
  });
  const templates = useQuery({
    queryKey: ["communications", "templates"],
    queryFn: () => apiFetch<{ items: EmailTemplateRecord[] }>("/api/communications/templates")
  });
  const locations = useQuery({
    queryKey: ["communications", "automation-locations"],
    queryFn: () => apiFetch<{ items: LocationSettingsItem[] }>("/api/settings/locations")
  });
  const services = useQuery({
    queryKey: ["communications", "automation-services"],
    queryFn: () => apiFetch<{ items: ServiceSettingsItem[] }>("/api/settings/services")
  });
  const tags = useQuery({
    queryKey: ["communications", "automation-tags"],
    queryFn: () => apiFetch<{ items: TagSettingsItem[] }>("/api/settings/tags")
  });
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<EmailAutomationInput>(emptyAutomationDraft);

  const automationItems = asArray<EmailAutomationRecord>(automations.data?.items);
  const templateItems = asArray<EmailTemplateRecord>(templates.data?.items);
  const locationItems = asArray<LocationSettingsItem>(locations.data?.items);
  const serviceItems = asArray<ServiceSettingsItem>(services.data?.items);
  const tagItems = asArray<TagSettingsItem>(tags.data?.items);
  const marketingTemplates = templateItems.filter((item) => item.templateType === "marketing");
  const activeLocations = locationItems.filter((item) => item.active !== 0 && item.active !== false);
  const activeServices = serviceItems.filter((item) => item.active !== 0 && item.active !== false);
  const activeCustomerTags = tagItems.filter(
    (item) => (item.entityType ?? "customer") === "customer" && item.active !== 0 && item.active !== false
  );
  const sectionError = automations.error ?? templates.error ?? locations.error ?? services.error ?? tags.error;
  const defaultAutomationTemplateId = draft.templateId || marketingTemplates[0]?.id || "";
  const automationSteps = getAutomationSequenceSteps(draft);
  const canSaveAutomation = Boolean(defaultAutomationTemplateId && automationSteps.length);
  const automationDraftForSave = normalizeClientAutomationDraft(
    {
      ...draft,
      templateId: defaultAutomationTemplateId
    },
    defaultAutomationTemplateId
  );

  useEffect(() => {
    if (automationItems.length && !selectedId) {
      setSelectedId(automationItems[0].id);
    }
  }, [automationItems, selectedId]);

  useEffect(() => {
    if (!selectedId && marketingTemplates.length && !draft.templateId) {
      setDraft((current) => normalizeClientAutomationDraft(current, marketingTemplates[0].id));
    }
  }, [selectedId, marketingTemplates, draft.templateId]);

  useEffect(() => {
    const selected = automationItems.find((item) => item.id === selectedId);
    if (selected) {
      setDraft(normalizeClientAutomationDraft(selected, selected.templateId || marketingTemplates[0]?.id || ""));
    } else {
      setDraft((current) => normalizeClientAutomationDraft(current, current.templateId || marketingTemplates[0]?.id || ""));
    }
  }, [automationItems, selectedId, marketingTemplates]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<EmailAutomationRecord>(selectedId ? `/api/communications/automations/${selectedId}` : "/api/communications/automations", {
        method: selectedId ? "PUT" : "POST",
        body: JSON.stringify(automationDraftForSave)
      }),
    onSuccess: async (automation) => {
      setSelectedId(automation.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "automations"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch<{ success: boolean }>(`/api/communications/automations/${selectedId}`, { method: "DELETE" }),
    onSuccess: async () => {
      setSelectedId("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "automations"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const pauseMutation = useMutation({
    mutationFn: () => apiFetch<EmailAutomationRecord>(`/api/communications/automations/${selectedId}/pause`, { method: "POST" }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "automations"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const resumeMutation = useMutation({
    mutationFn: () => apiFetch<EmailAutomationRecord>(`/api/communications/automations/${selectedId}/resume`, { method: "POST" }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "automations"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const selectedAutomation = automationItems.find((item) => item.id === selectedId) ?? null;
  const automationSequenceSteps = automationSteps;
  const applyAutomationPreset = (preset: "welcome" | "upsell" | "winback") => {
    const baseTemplateId = defaultAutomationTemplateId || marketingTemplates[0]?.id || "";
    const sourceServiceId = activeServices[0]?.id ?? "";
    const targetServiceId = activeServices.find((item) => item.id !== sourceServiceId)?.id ?? "";
    const nextDraft: EmailAutomationInput =
      preset === "welcome"
        ? {
            ...emptyAutomationDraft,
            name: "Welcome New Customer",
            templateId: baseTemplateId,
            triggerType: "first_completed_booking",
            delayDays: 1,
            followUpDelays: [],
            stepTemplateIds: {},
            steps: [{ id: "step_1", delayValue: 1, delayUnit: "hours", templateId: baseTemplateId, generatedOffer: normalizeGeneratedOffer({}, "customer_winback") }],
            enrollmentPolicy: "future_only",
            audienceFilters: normalizeClientAutomationAudienceFilters({}),
            active: true
          }
        : preset === "upsell"
          ? {
              ...emptyAutomationDraft,
              name: "Service Upsell",
              templateId: baseTemplateId,
              triggerType: "service_upsell_after_completed_booking",
              delayDays: 7,
              followUpDelays: [21],
              stepTemplateIds: {},
              steps: [
                { id: "step_1", delayValue: 7, delayUnit: "days", templateId: baseTemplateId, generatedOffer: normalizeGeneratedOffer({}, "customer_winback") },
                { id: "step_2", delayValue: 21, delayUnit: "days", templateId: baseTemplateId, generatedOffer: normalizeGeneratedOffer({}, "customer_winback") }
              ],
              enrollmentPolicy: "future_only",
              audienceFilters: normalizeClientAutomationAudienceFilters({
                sourceServiceIds: sourceServiceId ? [sourceServiceId] : [],
                targetServiceIds: targetServiceId ? [targetServiceId] : []
              }),
              active: true
            }
          : {
              ...emptyAutomationDraft,
              name: "Win Back Customer",
              templateId: baseTemplateId,
              triggerType: "days_since_last_completed_booking",
              delayDays: 60,
              followUpDelays: [90, 120, 180, 360],
              stepTemplateIds: {},
              steps: [
                { id: "step_1", delayValue: 60, delayUnit: "days", templateId: baseTemplateId, generatedOffer: normalizeGeneratedOffer({}, "customer_winback") },
                { id: "step_2", delayValue: 90, delayUnit: "days", templateId: baseTemplateId, generatedOffer: { ...normalizeGeneratedOffer({}, "customer_winback"), enabled: true, discountValue: 10, codePrefix: "WB90", expirationDays: 7 } },
                { id: "step_3", delayValue: 120, delayUnit: "days", templateId: baseTemplateId, generatedOffer: { ...normalizeGeneratedOffer({}, "customer_winback"), enabled: true, discountValue: 10, codePrefix: "WB120", expirationDays: 7 } },
                { id: "step_4", delayValue: 180, delayUnit: "days", templateId: baseTemplateId, generatedOffer: { ...normalizeGeneratedOffer({}, "customer_winback"), enabled: true, discountValue: 15, codePrefix: "WB180", expirationDays: 7 } },
                { id: "step_5", delayValue: 360, delayUnit: "days", templateId: baseTemplateId, generatedOffer: { ...normalizeGeneratedOffer({}, "customer_winback"), enabled: true, discountValue: 15, codePrefix: "WB360", expirationDays: 7 } }
              ],
              enrollmentPolicy: "future_only",
              audienceFilters: normalizeClientAutomationAudienceFilters({}),
              active: true
            };

    setSelectedId("");
    setDraft(normalizeClientAutomationDraft(nextDraft, baseTemplateId));
  };

  if (sectionError) {
    return (
      <Card title="Automations unavailable" eyebrow="Re-engagement">
        <p className="error-text">{sectionError instanceof Error ? sectionError.message : "Unable to load automations right now."}</p>
      </Card>
    );
  }

  return (
    <div className="communications-shell">
      <Card
        title="Automations"
        eyebrow="Re-engagement"
        actions={
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedId("");
              setDraft(normalizeClientAutomationDraft(emptyAutomationDraft, marketingTemplates[0]?.id || ""));
            }}
          >
            New automation
          </Button>
        }
      >
        <div className="communications-preset-grid">
          <button type="button" className="communications-preset" onClick={() => applyAutomationPreset("welcome")}>
            <strong>Welcome new customer</strong>
            <span>1 hour after the first completed visit</span>
          </button>
          <button type="button" className="communications-preset" onClick={() => applyAutomationPreset("upsell")}>
            <strong>Upsell service</strong>
            <span>After customers use one service but not another</span>
          </button>
          <button type="button" className="communications-preset" onClick={() => applyAutomationPreset("winback")}>
            <strong>Win back customer</strong>
            <span>60, 90, 120, 180, and 360 days after last service</span>
          </button>
        </div>
        <div className="communications-list">
          {automationItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`communications-list__item ${selectedId === item.id ? "is-active" : ""}`}
              onClick={() => setSelectedId(item.id)}
            >
              <div>
                <strong>{item.name}</strong>
                <p>{describeAutomationTrigger(item.triggerType, item.delayDays)} - {describeAutomationSequence(item)}</p>
              </div>
              <Badge tone={item.active ? "success" : "warning"}>{item.active ? "Active" : "Paused"}</Badge>
            </button>
          ))}
        </div>
      </Card>

      <div className="stack">
        <Card
          title={selectedId ? "Edit Automation" : "Create Automation"}
          eyebrow="Rule"
          actions={
            <div className="communications-card-actions">
              {selectedId ? (
                <Button variant="ghost" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending || saveMutation.isPending}>
                  Delete
                </Button>
              ) : null}
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !canSaveAutomation}>
                {saveMutation.isPending ? "Saving..." : "Save automation"}
              </Button>
            </div>
          }
        >
          {!marketingTemplates.length ? (
            <p className="error-text">Create at least one marketing template in Templates before saving an automation.</p>
          ) : null}
          <div className="grid two">
            <Field label="Automation name">
              <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </Field>
            <Field label="Trigger">
              <select
                value={draft.triggerType}
                onChange={(event) => setDraft({ ...draft, triggerType: event.target.value as EmailAutomationInput["triggerType"] })}
              >
                <option value="first_completed_booking">After first completed visit</option>
                <option value="service_upsell_after_completed_booking">Service upsell after visit</option>
                <option value="days_since_last_completed_booking">Days since last completed booking</option>
                <option value="days_since_cancelled_booking">Days since cancelled booking</option>
                <option value="days_since_no_show">Days since no-show</option>
              </select>
            </Field>
            <Field label="Active">
              <label className="checkbox-row">
                <input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} />
                <span>Keep generating lifecycle enrollments.</span>
              </label>
            </Field>
            <Field label="Enrollment">
              <select
                value={draft.enrollmentPolicy ?? "all_eligible"}
                onChange={(event) =>
                  setDraft({ ...draft, enrollmentPolicy: event.target.value === "future_only" ? "future_only" : "all_eligible" })
                }
              >
                <option value="all_eligible">All eligible customers</option>
                <option value="future_only">Only future trigger events</option>
              </select>
            </Field>
          </div>
          <div className="stack">
            <div>
              <p className="eyebrow">Follow-up steps</p>
            </div>
            <div className="communications-step-template-list">
              {automationSequenceSteps.map((step) => {
                return (
                  <div key={step.id || step.stepNumber} className="communications-step-template-row">
                    <div>
                      <strong>Step {step.stepNumber}</strong>
                      <div className="communications-step-delay-controls">
                        <input
                          type="number"
                          min="1"
                          max="3650"
                          value={step.delayValue}
                          onChange={(event) => {
                            const nextSteps = draft.steps.map((item) =>
                              item.id === step.id ? { ...item, delayValue: Math.max(1, Number(event.target.value || 0) || 1) } : item
                            );
                            setDraft(normalizeClientAutomationDraft({ ...draft, steps: nextSteps }, draft.templateId || marketingTemplates[0]?.id || ""));
                          }}
                        />
                        <select
                          value={step.delayUnit}
                          onChange={(event) => {
                            const nextSteps = draft.steps.map((item) =>
                              item.id === step.id
                                ? { ...item, delayUnit: event.target.value as EmailAutomationInput["steps"][number]["delayUnit"] }
                                : item
                            );
                            setDraft(normalizeClientAutomationDraft({ ...draft, steps: nextSteps }, draft.templateId || marketingTemplates[0]?.id || ""));
                          }}
                        >
                          <option value="minutes">Minutes</option>
                          <option value="hours">Hours</option>
                          <option value="days">Days</option>
                        </select>
                      </div>
                      <p>{formatAutomationDelay(step)} after the triggering event</p>
                    </div>
                    <div className="communications-step-actions">
                      <select
                        value={step.templateId || defaultAutomationTemplateId}
                        onChange={(event) => {
                          const nextTemplateId = event.target.value;
                          const nextSteps = draft.steps.map((item) => (item.id === step.id ? { ...item, templateId: nextTemplateId } : item));
                          setDraft(
                            normalizeClientAutomationDraft(
                              {
                                ...draft,
                                templateId: step.stepNumber === 1 ? nextTemplateId : draft.templateId,
                                steps: nextSteps
                              },
                              nextTemplateId || draft.templateId || marketingTemplates[0]?.id || ""
                            )
                          );
                        }}
                      >
                        <option value="">Choose a template</option>
                        {marketingTemplates.map((item) => (
                          <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                      </select>
                      {automationSequenceSteps.length > 1 ? (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            const nextSteps = draft.steps.filter((item) => item.id !== step.id);
                            setDraft(normalizeClientAutomationDraft({ ...draft, steps: nextSteps }, draft.templateId || marketingTemplates[0]?.id || ""));
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </div>
                    <div className="generated-offer-controls">
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={Boolean(step.generatedOffer?.enabled)}
                          onChange={(event) => {
                            const nextSteps = draft.steps.map((item) =>
                              item.id === step.id
                                ? {
                                    ...item,
                                    generatedOffer: {
                                      ...normalizeGeneratedOffer(item.generatedOffer, "customer_winback"),
                                      enabled: event.target.checked,
                                      eligibilityMode: "customer_winback" as const
                                    }
                                  }
                                : item
                            );
                            setDraft(normalizeClientAutomationDraft({ ...draft, steps: nextSteps }, draft.templateId || marketingTemplates[0]?.id || ""));
                          }}
                        />
                        <span>Generated promo code</span>
                      </label>
                      {step.generatedOffer?.enabled ? (
                        <div className="grid three">
                          <Field label="Discount %">
                            <input
                              type="number"
                              min="1"
                              max="100"
                              value={step.generatedOffer.discountValue}
                              onChange={(event) => {
                                const nextSteps = draft.steps.map((item) =>
                                  item.id === step.id
                                    ? {
                                        ...item,
                                        generatedOffer: {
                                          ...normalizeGeneratedOffer(item.generatedOffer, "customer_winback"),
                                          discountValue: Number(event.target.value || 10),
                                          eligibilityMode: "customer_winback" as const
                                        }
                                      }
                                    : item
                                );
                                setDraft(normalizeClientAutomationDraft({ ...draft, steps: nextSteps }, draft.templateId || marketingTemplates[0]?.id || ""));
                              }}
                            />
                          </Field>
                          <Field label="Prefix">
                            <input
                              value={step.generatedOffer.codePrefix}
                              onChange={(event) => {
                                const nextSteps = draft.steps.map((item) =>
                                  item.id === step.id
                                    ? {
                                        ...item,
                                        generatedOffer: {
                                          ...normalizeGeneratedOffer(item.generatedOffer, "customer_winback"),
                                          codePrefix: event.target.value,
                                          eligibilityMode: "customer_winback" as const
                                        }
                                      }
                                    : item
                                );
                                setDraft(normalizeClientAutomationDraft({ ...draft, steps: nextSteps }, draft.templateId || marketingTemplates[0]?.id || ""));
                              }}
                            />
                          </Field>
                          <Field label="Expires in days">
                            <input
                              type="number"
                              min="1"
                              max="365"
                              value={step.generatedOffer.expirationDays}
                              onChange={(event) => {
                                const nextSteps = draft.steps.map((item) =>
                                  item.id === step.id
                                    ? {
                                        ...item,
                                        generatedOffer: {
                                          ...normalizeGeneratedOffer(item.generatedOffer, "customer_winback"),
                                          expirationDays: Number(event.target.value || 7),
                                          eligibilityMode: "customer_winback" as const
                                        }
                                      }
                                    : item
                                );
                                setDraft(normalizeClientAutomationDraft({ ...draft, steps: nextSteps }, draft.templateId || marketingTemplates[0]?.id || ""));
                              }}
                            />
                          </Field>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              variant="secondary"
              onClick={() => {
                const lastStep = automationSequenceSteps[automationSequenceSteps.length - 1];
                const nextStep = {
                  id: `step_${Date.now()}`,
                  delayValue: lastStep?.delayUnit === "minutes" ? Math.max(1, (lastStep.delayValue ?? 1) + 30) : Math.max(1, (lastStep?.delayValue ?? 1) + 1),
                  delayUnit: lastStep?.delayUnit ?? "days",
                  templateId: lastStep?.templateId || draft.templateId || marketingTemplates[0]?.id || "",
                  generatedOffer: normalizeGeneratedOffer({}, "customer_winback")
                };
                setDraft(normalizeClientAutomationDraft({ ...draft, steps: [...draft.steps, nextStep] }, draft.templateId || marketingTemplates[0]?.id || ""));
              }}
            >
              Add step
            </Button>
          </div>
          {draft.triggerType === "service_upsell_after_completed_booking" ? (
            <div className="grid two">
              <div className="stack">
                <div>
                  <p className="eyebrow">Customers who booked</p>
                </div>
                <div className="tag-grid">
                  {activeServices.length ? (
                    activeServices.map((service) => {
                      const active = draft.audienceFilters.sourceServiceIds.includes(service.id);
                      return (
                        <label key={service.id} className={`tag-choice ${active ? "is-active" : ""}`}>
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={(event) => {
                              const next = event.target.checked
                                ? [...draft.audienceFilters.sourceServiceIds, service.id]
                                : draft.audienceFilters.sourceServiceIds.filter((value) => value !== service.id);
                              setDraft({
                                ...draft,
                                audienceFilters: {
                                  ...draft.audienceFilters,
                                  sourceServiceIds: next
                                }
                              });
                            }}
                          />
                          <span>{service.name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="card-copy">No active services are available yet.</p>
                  )}
                </div>
              </div>
              <div className="stack">
                <div>
                  <p className="eyebrow">But have not booked</p>
                </div>
                <div className="tag-grid">
                  {activeServices.length ? (
                    activeServices.map((service) => {
                      const active = draft.audienceFilters.targetServiceIds.includes(service.id);
                      return (
                        <label key={service.id} className={`tag-choice ${active ? "is-active" : ""}`}>
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={(event) => {
                              const next = event.target.checked
                                ? [...draft.audienceFilters.targetServiceIds, service.id]
                                : draft.audienceFilters.targetServiceIds.filter((value) => value !== service.id);
                              setDraft({
                                ...draft,
                                audienceFilters: {
                                  ...draft.audienceFilters,
                                  targetServiceIds: next
                                }
                              });
                            }}
                          />
                          <span>{service.name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="card-copy">No active services are available yet.</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}
          <div className="stack">
            <div>
              <p className="eyebrow">Customer tags</p>
              <p className="card-copy">When selected, the automation only targets customers matching at least one of these tags.</p>
            </div>
            <div className="tag-grid">
              {activeCustomerTags.length ? (
                activeCustomerTags.map((tag) => {
                  const active = draft.audienceFilters.tagIds.includes(tag.id);
                  return (
                    <label key={tag.id} className={`tag-choice ${active ? "is-active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...draft.audienceFilters.tagIds, tag.id]
                            : draft.audienceFilters.tagIds.filter((value) => value !== tag.id);
                          setDraft({
                            ...draft,
                            audienceFilters: {
                              ...draft.audienceFilters,
                              tagIds: next
                            }
                          });
                        }}
                      />
                      <span className="tag-swatch" style={{ backgroundColor: tag.color || "#c65f3f" }} />
                      <span>{tag.name}</span>
                    </label>
                  );
                })
              ) : (
                <p className="card-copy">No active customer tags are available yet.</p>
              )}
            </div>
          </div>
          <Field label="Service frequencies" hint="Comma-separated values such as Weekly, Biweekly, Monthly. Leave blank for all frequencies.">
            <input
              value={formatFilterList(draft.audienceFilters.serviceFrequencies)}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  audienceFilters: {
                    ...draft.audienceFilters,
                    serviceFrequencies: parseFilterList(event.target.value)
                  }
                })
              }
            />
          </Field>
          <div className="stack">
            <div>
              <p className="eyebrow">Locations</p>
              <p className="card-copy">Match the location from the booking event that triggered this automation.</p>
            </div>
            <div className="tag-grid">
              {activeLocations.length ? (
                activeLocations.map((location) => {
                  const active = draft.audienceFilters.locationIds.includes(location.id);
                  return (
                    <label key={location.id} className={`tag-choice ${active ? "is-active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...draft.audienceFilters.locationIds, location.id]
                            : draft.audienceFilters.locationIds.filter((value) => value !== location.id);
                          setDraft({
                            ...draft,
                            audienceFilters: {
                              ...draft.audienceFilters,
                              locationIds: next
                            }
                          });
                        }}
                      />
                      <span>{location.name}</span>
                    </label>
                  );
                })
              ) : (
                <p className="card-copy">No active locations are available yet.</p>
              )}
            </div>
          </div>
          {saveMutation.isError ? <p className="error-text">{String(saveMutation.error)}</p> : null}
          {deleteMutation.isError ? <p className="error-text">{String(deleteMutation.error)}</p> : null}
        </Card>

        <Card title="Automation Status" eyebrow="Pipeline">
          {selectedAutomation ? (
            <div className="stack">
              <div className="communications-card-actions">
                {selectedAutomation.active ? (
                  <Button variant="secondary" onClick={() => pauseMutation.mutate()} disabled={pauseMutation.isPending}>
                    {pauseMutation.isPending ? "Pausing..." : "Pause automation"}
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={() => resumeMutation.mutate()} disabled={resumeMutation.isPending}>
                    {resumeMutation.isPending ? "Resuming..." : "Resume automation"}
                  </Button>
                )}
              </div>
              <div className="grid three">
                <div className="booking-meta-card">
                  <p className="eyebrow">Queued next</p>
                  <strong>{selectedAutomation.nextDueAt ? formatDateTime(selectedAutomation.nextDueAt) : "No due enrollments"}</strong>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Pipeline</p>
                  <strong>{selectedAutomation.pendingCount} pending / {selectedAutomation.queuedCount} queued</strong>
                </div>
                <div className="booking-meta-card">
                  <p className="eyebrow">Results</p>
                  <strong>{selectedAutomation.sentCount} sent / {selectedAutomation.failedCount} failed</strong>
                  <p className="card-copy">{selectedAutomation.cancelledCount} skipped</p>
                </div>
              </div>
              <p className="card-copy">
                This automation sends its configured sequence per customer {describeAutomationTrigger(selectedAutomation.triggerType, selectedAutomation.delayDays)}.
              </p>
              <p className="card-copy">Sequence: {describeAutomationSequence(selectedAutomation)}</p>
              <div className="communications-step-template-list">
                {getAutomationSequenceSteps(selectedAutomation).map((step) => {
                  const templateId = step.templateId || selectedAutomation.templateId;
                  const templateName = marketingTemplates.find((item) => item.id === templateId)?.name || "Default template";
                  return (
                    <div key={step.stepNumber} className="communications-step-template-row">
                      <div>
                        <strong>Step {step.stepNumber}</strong>
                        <p>{formatAutomationDelay(step)} after trigger</p>
                      </div>
                      <p className="card-copy">{templateName}</p>
                    </div>
                  );
                })}
              </div>
              <div className="stack">
                <div>
                  <p className="eyebrow">Per-step performance</p>
                  <p className="card-copy">Track how each message in the sequence is progressing through pending, queued, sent, and failed states.</p>
                </div>
                <div className="communications-step-performance-grid">
                  {selectedAutomation.stepStats.map((step) => (
                    <div key={step.stepNumber} className="booking-meta-card">
                      <p className="eyebrow">Step {step.stepNumber}</p>
                      <strong>{formatDelayMinutes(step.stepDelayMinutes || step.stepDelayDays * 24 * 60)}</strong>
                      <p className="card-copy">Next due: {step.nextDueAt ? formatDateTime(step.nextDueAt) : "None queued"}</p>
                      <p className="card-copy">Pipeline: {step.pendingCount} pending / {step.queuedCount} queued</p>
                      <p className="card-copy">Results: {step.sentCount} sent / {step.failedCount} failed / {step.cancelledCount} skipped</p>
                      {selectedAutomation.triggerType === "first_completed_booking" ? (
                        <p className="card-copy">Engagement appears in analytics after delivery events arrive.</p>
                      ) : (
                        <p className="card-copy">
                          {selectedAutomation.triggerType === "service_upsell_after_completed_booking" ? "Target booked" : "Rebooked"}: {step.convertedCount} / {step.sentCount} sent ({formatPercentage(step.conversionRate)})
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <p className="card-copy">
                Filters: {describeAutomationFilters(selectedAutomation.audienceFilters)}
              </p>
            </div>
          ) : (
            <p className="card-copy">Create an automation to start enrolling past customers into remarketing sends.</p>
          )}
          {pauseMutation.isError ? <p className="error-text">{String(pauseMutation.error)}</p> : null}
          {resumeMutation.isError ? <p className="error-text">{String(resumeMutation.error)}</p> : null}
        </Card>
      </div>
    </div>
  );
}

function CommunicationsAudienceSection() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState<MarketingSubscriberInput>(emptySubscriberDraft);
  const [importText, setImportText] = useState("");

  const subscribers = useQuery({
    queryKey: ["communications", "subscribers", search],
    queryFn: () => apiFetch<{ items: MarketingSubscriberRecord[] }>(`/api/communications/subscribers?q=${encodeURIComponent(search)}`)
  });
  const suppressions = useQuery({
    queryKey: ["communications", "suppressions"],
    queryFn: () => apiFetch<{ items: EmailSuppressionRecord[] }>("/api/communications/suppressions")
  });

  useEffect(() => {
    if (subscribers.data?.items?.length && !selectedId) {
      setSelectedId(subscribers.data.items[0].id);
    }
  }, [subscribers.data, selectedId]);

  useEffect(() => {
    const selected = subscribers.data?.items.find((item) => item.id === selectedId);
    if (selected) {
      setDraft({
        email: selected.email,
        firstName: selected.firstName,
        lastName: selected.lastName,
        status: selected.status,
        source: selected.source,
        sourceDetails: selected.sourceDetails,
        tags: selected.tags
      });
    } else {
      setDraft(emptySubscriberDraft);
    }
  }, [selectedId, subscribers.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<MarketingSubscriberRecord>(selectedId ? `/api/communications/subscribers/${selectedId}` : "/api/communications/subscribers", {
        method: selectedId ? "PUT" : "POST",
        body: JSON.stringify(draft)
      }),
    onSuccess: async (subscriber) => {
      setSelectedId(subscriber.id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "subscribers"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "suppressions"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const importMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ imported: number }>("/api/communications/subscribers/import", {
        method: "POST",
        body: JSON.stringify({
          text: importText,
          source: "admin_import"
        })
      }),
    onSuccess: async () => {
      setImportText("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "subscribers"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  const unsuppressMutation = useMutation({
    mutationFn: ({ suppressionId, reason }: { suppressionId: string; reason: string }) =>
      apiFetch<{ success: boolean }>(`/api/communications/suppressions/${suppressionId}`, {
        method: "DELETE",
        body: JSON.stringify({ reason })
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["communications", "suppressions"] }),
        queryClient.invalidateQueries({ queryKey: ["communications", "overview"] })
      ]);
    }
  });

  return (
    <div className="communications-shell">
      <Card
        title="Subscribers"
        eyebrow="Audience"
        actions={<Button variant="secondary" onClick={() => setSelectedId("")}>New subscriber</Button>}
      >
        <Field label="Search">
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by name or email" />
        </Field>
        <div className="communications-list">
          {(subscribers.data?.items ?? []).map((item) => (
            <button
              key={item.id}
              type="button"
              className={`communications-list__item ${selectedId === item.id ? "is-active" : ""}`}
              onClick={() => setSelectedId(item.id)}
            >
              <div>
                <strong>{item.firstName || item.lastName ? `${item.firstName} ${item.lastName}`.trim() : item.email}</strong>
                <p>{item.email}</p>
              </div>
              <Badge tone={item.status === "subscribed" ? "success" : item.status === "suppressed" ? "danger" : "warning"}>
                {item.status}
              </Badge>
            </button>
          ))}
        </div>
      </Card>

      <div className="stack">
        <Card
          title={selectedId ? "Edit Subscriber" : "Add Subscriber"}
          eyebrow="Consent"
          actions={<Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>{saveMutation.isPending ? "Saving..." : "Save subscriber"}</Button>}
        >
          <div className="grid two">
            <Field label="Email"><input value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} /></Field>
            <Field label="Status">
              <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value as MarketingSubscriberInput["status"] })}>
                <option value="subscribed">Subscribed</option>
                <option value="unsubscribed">Unsubscribed</option>
                <option value="suppressed">Suppressed</option>
              </select>
            </Field>
            <Field label="First name"><input value={draft.firstName} onChange={(event) => setDraft({ ...draft, firstName: event.target.value })} /></Field>
            <Field label="Last name"><input value={draft.lastName} onChange={(event) => setDraft({ ...draft, lastName: event.target.value })} /></Field>
            <Field label="Source"><input value={draft.source} onChange={(event) => setDraft({ ...draft, source: event.target.value })} /></Field>
            <Field label="Tags">
              <input value={tagsToText(draft.tags)} onChange={(event) => setDraft({ ...draft, tags: parseTagsInput(event.target.value) })} />
            </Field>
          </div>
          {saveMutation.isError ? <p className="error-text">{String(saveMutation.error)}</p> : null}
        </Card>

        <Card title="Import Subscribers" eyebrow="Bulk add">
          <Field label="Email list" hint="Paste one email per line or separate them with commas.">
            <textarea rows={6} value={importText} onChange={(event) => setImportText(event.target.value)} />
          </Field>
          <Button onClick={() => importMutation.mutate()} disabled={!importText.trim() || importMutation.isPending}>
            {importMutation.isPending ? "Importing..." : "Import subscribers"}
          </Button>
          {importMutation.isError ? <p className="error-text">{String(importMutation.error)}</p> : null}
        </Card>

        <Card title="Suppression List" eyebrow="Do not send">
          {(suppressions.data?.items ?? []).length ? (
            <div className="communications-suppression-list">
              {suppressions.data?.items.map((item) => (
                <div key={item.id} className="communications-suppression-row">
                  <div>
                    <strong>{item.email}</strong>
                    <p>{item.reason} via {item.source} on {formatDateTime(item.createdAt)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const confirmed = window.confirm(
                        `Remove suppression for ${item.email}? This will re-enable marketing eligibility unless the customer is opted out elsewhere.`
                      );
                      if (!confirmed) {
                        return;
                      }
                      const reason = window.prompt("Reason for removing this suppression", "Customer requested resubscribe") ?? "";
                      if (!reason.trim()) {
                        return;
                      }
                      unsuppressMutation.mutate({ suppressionId: item.id, reason: reason.trim() });
                    }}
                    disabled={unsuppressMutation.isPending}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="card-copy">No addresses are currently suppressed.</p>
          )}
          {unsuppressMutation.isError ? <p className="error-text">{String(unsuppressMutation.error)}</p> : null}
        </Card>
      </div>
    </div>
  );
}

function CommunicationsActivitySection() {
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [deliveryFilter, setDeliveryFilter] = useState("all");
  const [sendFilter, setSendFilter] = useState("all");
  const activity = useQuery({
    queryKey: ["communications", "activity", "full"],
    queryFn: () => apiFetch<{ items: CommunicationActivityRow[] }>("/api/communications/activity?limit=200")
  });

  const searchValue = search.trim().toLowerCase();
  const items = activity.data?.items ?? [];
  const filteredItems = items.filter((item) => {
    const itemSource = getSourceFilterValue(item);
    if (sourceFilter !== "all" && itemSource !== sourceFilter) {
      return false;
    }

    const itemDeliveryStatus = String(item.deliveryStatus || item.status).trim().toLowerCase();
    if (deliveryFilter !== "all" && itemDeliveryStatus !== deliveryFilter) {
      return false;
    }

    const itemSendStatus = String(item.status ?? "").trim().toLowerCase();
    if (sendFilter !== "all" && itemSendStatus !== sendFilter) {
      return false;
    }

    if (!searchValue) {
      return true;
    }

    const haystack = [
      item.recipient,
      item.templateKey,
      item.subjectRendered,
      item.errorMessage,
      getSourceFilterLabel(itemSource),
      item.channel,
      item.messageKind
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(searchValue);
  });

  const hasFilters = Boolean(searchValue) || sourceFilter !== "all" || deliveryFilter !== "all" || sendFilter !== "all";

  return (
    <Card
      title="Delivery Activity"
      eyebrow="Unified log"
      actions={
        <div className="communications-activity-meta">
          <span>
            Showing {filteredItems.length} of {items.length}
          </span>
          {hasFilters ? (
            <Button
              variant="ghost"
              onClick={() => {
                setSearch("");
                setSourceFilter("all");
                setDeliveryFilter("all");
                setSendFilter("all");
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="communications-activity-toolbar">
        <Field label="Search" hint="Recipient, template, subject, or error text.">
          <input
            className="communications-activity-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search activity"
          />
        </Field>
        <Field label="Source">
          <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}>
            <option value="all">All sources</option>
            <option value="transactional">Transactional</option>
            <option value="campaign">Campaigns</option>
            <option value="automation">Automations</option>
            <option value="marketing">Other marketing</option>
          </select>
        </Field>
        <Field label="Delivery">
          <select value={deliveryFilter} onChange={(event) => setDeliveryFilter(event.target.value)}>
            <option value="all">All delivery states</option>
            <option value="delivered">Delivered</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="sent">Sent</option>
            <option value="delivery_delayed">Delayed</option>
            <option value="failed">Failed</option>
            <option value="bounced">Bounced</option>
            <option value="complained">Complained</option>
          </select>
        </Field>
        <Field label="Send">
          <select value={sendFilter} onChange={(event) => setSendFilter(event.target.value)}>
            <option value="all">All send states</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </Field>
      </div>
      {filteredItems.length ? (
        <DataTable
          columns={["When", "Kind", "Channel", "Recipient", "Template", "Delivery", "Send", "Error"]}
          rows={filteredItems.map((item) => [
            formatDateTime(item.bucket),
            getSourceFilterLabel(getSourceFilterValue(item)),
            item.channel,
            item.recipient || "-",
            item.templateKey,
            <Badge key={`${item.id}-delivery`} tone={getDeliveryTone(item.deliveryStatus || item.status)}>
              {item.deliveryStatus || item.status}
            </Badge>,
            <Badge
              key={`${item.id}-send`}
              tone={item.status === "sent" ? "success" : item.status === "failed" ? "danger" : "neutral"}
            >
              {item.status}
            </Badge>,
            item.errorMessage || "-"
          ])}
        />
      ) : items.length ? (
        <p className="card-copy">No activity matches the current filters.</p>
      ) : (
        <p className="card-copy">No communications have been recorded yet.</p>
      )}
    </Card>
  );
}
